'use client'

import { useState, useMemo, useCallback, useEffect } from 'react'
// Reads the Supabase `people` table to decide which linked people get a chip. #130 owns whether that table still holds anything.
// eslint-disable-next-line import/no-restricted-paths
import { usePeople } from '@/legacy/supabase-store/use-people'
import { useTopics } from '@/topics'
import { useEntryUpdates } from '@/capture'
import { useQueryClient } from '@tanstack/react-query'
import { EmptyState } from '@/ui/section-header'
import type { ViewMode } from '@/ui/view-switcher'
import { WindIcon, FunnelSimpleIcon } from '@phosphor-icons/react'

// Import extracted hooks and components
import { useFeedEntries } from './use-feed-entries'
import { useFeedMutations } from './use-feed-mutations'
import { FeedHeader } from './feed-header'
import { PaginatedFeed } from './paginated-feed'
import { TimelineView } from './timeline-view'

// Import row components
import { FeedRow } from '@/entries'

import type { FilterType, EntryWithPeople, FeedEntry } from './types'
import type { PromptCategory } from '@/entries'

interface UnifiedFeedProps {
  journals: EntryWithPeople[]
  tasks: EntryWithPeople[]
  movies?: EntryWithPeople[]
  books?: EntryWithPeople[]
  artists?: EntryWithPeople[]
  labels?: EntryWithPeople[]
  releases?: EntryWithPeople[]
  searchQuery?: string
  rawSearchQuery?: string
  selectedTopics?: string[]
  onSearchChange?: (query: string) => void
  onTopicsChange?: (topics: string[]) => void
  showCompleted?: boolean
  onShowCompletedChange?: (show: boolean) => void
  viewMode?: ViewMode
  onViewModeChange?: (mode: ViewMode) => void
  availableViewModes?: ViewMode[]
  projectId?: string
  /** Default entry type filter (overrides internal default of 'all') */
  defaultFilter?: FilterType
  /** Filter journal entries by prompt category */
  promptCategoryFilter?: PromptCategory
}

/**
 * Unified feed showing all activity (journals, tasks, library items)
 */
export function UnifiedFeed({
  journals,
  tasks,
  movies = [],
  books = [],
  artists = [],
  labels = [],
  releases = [],
  searchQuery = '',
  rawSearchQuery,
  selectedTopics = [],
  onSearchChange,
  onTopicsChange,
  showCompleted = false,
  onShowCompletedChange,
  viewMode = 'list',
  onViewModeChange,
  availableViewModes,
  projectId,
  defaultFilter = 'all',
  promptCategoryFilter,
}: UnifiedFeedProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [filter, setFilter] = useState<FilterType>(defaultFilter)

  // Sync filter state when defaultFilter prop changes (e.g. journal page toggle)
  useEffect(() => {
    setFilter(defaultFilter)
  }, [defaultFilter])

  const { data: availablePeople = [] } = usePeople()
  const { topics: availableTopics = [] } = useTopics()
  const taskTopics = useMemo(
    () => availableTopics.filter((t) => t.entryType === 'task'),
    [availableTopics],
  )
  const { subscribe } = useEntryUpdates()
  const queryClient = useQueryClient()

  // Listen for real-time entry updates and refresh feed
  useEffect(() => {
    const unsubscribe = subscribe(() => {
      // Invalidate feed queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['entries'] })
    })

    return unsubscribe
  }, [subscribe, queryClient])

  // Use extracted hooks
  const { feedEntries: unfilteredEntries } = useFeedEntries({
    journals,
    tasks,
    movies,
    books,
    artists,
    labels,
    releases,
    filter,
    showCompleted,
    projectId,
  })

  // Apply search and topic filters
  const feedEntries = useMemo(() => {
    let filtered = unfilteredEntries

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter((entry) => {
        const title = entry.data.title?.toLowerCase() || ''
        const content = entry.data.content?.toLowerCase() || ''
        const name =
          (entry.data.metadata as { name?: string })?.name?.toLowerCase() || ''
        return (
          title.includes(query) ||
          content.includes(query) ||
          name.includes(query)
        )
      })
    }

    // Apply topic filter
    if (selectedTopics.length > 0) {
      filtered = filtered.filter((entry) => {
        const topicIds = entry.data.topicIds || []
        return selectedTopics.some((topicId) => topicIds.includes(topicId))
      })
    }

    // Apply prompt category filter
    if (promptCategoryFilter) {
      filtered = filtered.filter((entry) => {
        if (entry.type !== 'journal') return false
        return entry.data.metadata.promptCategory === promptCategoryFilter
      })
    }

    return filtered
  }, [unfilteredEntries, searchQuery, selectedTopics, promptCategoryFilter])

  // Clear expandedId when the expanded entry is no longer in the feed (e.g. after deletion)
  useEffect(() => {
    if (expandedId && !feedEntries.some((e) => e.data.id === expandedId)) {
      setExpandedId(null)
    }
  }, [feedEntries, expandedId])

  const {
    deletingId,
    completingId,
    savingId,
    handleDeleteJournal,
    handleSaveJournal,
    handleDeleteTask,
    handleCompleteTask,
    handleSaveTask,
    handleChecklistToggle,
    getChecklistItems,
    handleDeleteMovie,
    handleSaveMovie,
    handleDeleteBook,
    handleSaveBook,
    handleDeleteArtist,
    handleSaveArtist,
    handleDeleteLabel,
    handleSaveLabel,
    handleDeleteRelease,
    handleSaveRelease,
  } = useFeedMutations()

  const toggleExpand = useCallback((id: string) => {
    setExpandedId((current) => (current === id ? null : id))
  }, [])

  // Build filter chips for topic and entry type selections
  const filterChips = useMemo(() => {
    const chips: Array<{ id: string; label: string; onRemove: () => void }> = []

    // Add topic chips
    selectedTopics.forEach((topicId) => {
      const topic = availableTopics.find((t) => t.id === topicId)
      chips.push({
        id: `topic-${topicId}`,
        label: topic?.name || topicId,
        onRemove: () => {
          if (onTopicsChange) {
            onTopicsChange(selectedTopics.filter((id) => id !== topicId))
          }
        },
      })
    })

    return chips
  }, [selectedTopics, availableTopics, onTopicsChange])

  // Handler to clear all topic filters
  const handleClearAllFilters = () => {
    if (onTopicsChange) {
      onTopicsChange([])
    }
  }

  // The feed's own write wiring, keyed by kind. `useFeedMutations` still owns it — that is `feed/`'s
  // in step 5 — but the seven-arm switch that used to pair each handler with a row component is
  // gone: the row comes from the registry now, and this map is the half that is still the feed's.
  const feedWrites = useMemo(
    () => ({
      journal: { remove: handleDeleteJournal, save: handleSaveJournal },
      task: { remove: handleDeleteTask, save: handleSaveTask },
      movie: { remove: handleDeleteMovie, save: handleSaveMovie },
      book: { remove: handleDeleteBook, save: handleSaveBook },
      artist: { remove: handleDeleteArtist, save: handleSaveArtist },
      label: { remove: handleDeleteLabel, save: handleSaveLabel },
      release: { remove: handleDeleteRelease, save: handleSaveRelease },
    }),
    [
      handleDeleteJournal,
      handleSaveJournal,
      handleDeleteTask,
      handleSaveTask,
      handleDeleteMovie,
      handleSaveMovie,
      handleDeleteBook,
      handleSaveBook,
      handleDeleteArtist,
      handleSaveArtist,
      handleDeleteLabel,
      handleSaveLabel,
      handleDeleteRelease,
      handleSaveRelease,
    ],
  )

  // Feed item renderer - matches PaginatedFeed render prop signature
  const renderFeedItem = useCallback(
    (entry: FeedEntry, _index: number) => {
      const key = `${entry.type}-${entry.data.id}`
      const isExpanded = expandedId === entry.data.id
      const isDeleting = deletingId === entry.data.id
      const isSaving = savingId === entry.data.id

      const writes = feedWrites[entry.type]
      if (!writes) return null

      return (
        <div key={key} className="animate-feed-item-enter">
          <FeedRow
            entry={entry.data}
            isExpanded={isExpanded}
            onToggle={() => toggleExpand(entry.data.id)}
            onDelete={() => writes.remove(entry.data.id)}
            onSave={(updates: never) => writes.save(entry.data.id, updates)}
            isDeleting={isDeleting}
            isSaving={isSaving}
          />
        </div>
      )
    },
    [expandedId, deletingId, savingId, toggleExpand, feedWrites],
  )

  // Welcome state for brand-new users (no data at all)
  if (unfilteredEntries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <WindIcon
          className="text-muted-foreground/20 mb-5 h-12 w-12"
          weight="thin"
        />
        <p className="text-muted-foreground/40 font-[family-name:var(--font-instrument-serif)] text-lg italic">
          Your space is ready
        </p>
        <p className="text-muted-foreground/50 mt-2 max-w-xs text-center text-sm">
          Capture a thought, add a task, or log something to your library using
          the field above.
        </p>
      </div>
    )
  }

  // Filtered empty — user has data but current filters yield nothing
  if (feedEntries.length === 0) {
    return (
      <div className="space-y-3">
        <FeedHeader
          filter={filter}
          onFilterChange={setFilter}
          searchQuery={rawSearchQuery || searchQuery}
          onSearchChange={onSearchChange}
          selectedTopics={selectedTopics}
          onTopicsChange={onTopicsChange}
          activeFilters={filterChips}
          onClearAllFilters={handleClearAllFilters}
          showCompleted={showCompleted}
          onToggleCompleted={onShowCompletedChange}
          resultCount={feedEntries.length}
          totalCount={unfilteredEntries.length}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          availableViewModes={availableViewModes}
        />
        <EmptyState
          message="Nothing matches your filters"
          description="Try adjusting your search or clearing filters."
          icon={<FunnelSimpleIcon className="h-6 w-6" weight="thin" />}
        />
      </div>
    )
  }

  // Render feed
  return (
    <div className="space-y-3">
      <FeedHeader
        filter={filter}
        onFilterChange={setFilter}
        searchQuery={rawSearchQuery || searchQuery}
        onSearchChange={onSearchChange}
        selectedTopics={selectedTopics}
        onTopicsChange={onTopicsChange}
        activeFilters={filterChips}
        onClearAllFilters={handleClearAllFilters}
        showCompleted={showCompleted}
        onToggleCompleted={onShowCompletedChange}
        resultCount={feedEntries.length}
        totalCount={unfilteredEntries.length}
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
        availableViewModes={availableViewModes}
      />

      {/* Conditional rendering based on view mode */}
      {viewMode === 'timeline' ? (
        <div className="sm:bg-card overflow-hidden sm:rounded-lg sm:border">
          <TimelineView
            entries={feedEntries}
            renderEntry={(entry) => renderFeedItem(entry, 0)}
            emptyState={
              <EmptyState
                message="Your activity will appear here"
                description="Start by adding a thought, task, or something to your library."
              />
            }
          />
        </div>
      ) : (
        <div className="sm:bg-card overflow-hidden sm:rounded-lg sm:border">
          <PaginatedFeed
            entries={feedEntries}
            renderItem={renderFeedItem}
            estimatedItemHeight={48}
          />
        </div>
      )}
    </div>
  )
}
